/**
 * /admin/entities — a Professional's clients (professional-scoped).
 * @file src/web/features/admin/pages/entities.tsx
 *
 * The professional admin creates Entities (their clients), each with up to two
 * fixed logins — Entity Admin and Entity Moderator — then imports Tally
 * data into them. List/create (credentials revealed once), inline-edit
 * sidecard with per-login reset, archive, delete (typed-name confirm),
 * and a per-entity "Import data" action.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Building2, Plus, Search, Archive, ArchiveRestore, Trash2, Pencil, X, Loader2,
  CheckCircle2, KeyRound, Upload, Database, ShieldCheck, UserX,
} from 'lucide-react';
import { SEO } from '../../../shared/components';
import { AdminPageHeader } from '../components/AdminPageHeader';
import { PageLoading } from '../components/PageLoading';
import { adminFetchJson } from '../lib/admin-api';
import { useCurrentRole } from '../../auth/hooks';
import { hasMinRole } from '../../../shared/utils/roles';
import { CredentialReveal } from './team';

interface Member { id: string; name: string | null; email: string; roleLabel: string; isActive: boolean; lastLogin: string | null }
interface Entity {
  id: string; name: string; status: string; ownerPhone: string | null;
  legalName: string | null; gstNumber: string | null; gstStatus: string | null;
  billingAddress: string | null; city: string | null; state: string | null; pincode: string | null;
  members: Member[]; companies: number; createdAt: string;
}
interface List { total: number; items: Entity[] }

/** What the server said, without the status code adminFetchJson puts in front. */
const said = (e: unknown, fallback: string) => (e instanceof Error && e.message.replace(/^\d+\s/, '')) || fallback;

const fmtDate = (s: string | null) => (s ? new Date(s).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

export default function EntitiesPage() {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [data, setData] = useState<List | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [creating, setCreating] = useState(false);
  const [view, setView] = useState<null | { entity: Entity; editing: boolean }>(null);
  const [reveal, setReveal] = useState<null | { title: string; creds: Array<{ role: string; email: string; tempPassword: string }> }>(null);
  const [confirm, setConfirm] = useState<null | { kind: 'delete' | 'archive'; entity: Entity }>(null);
  const [failed, setFailed] = useState<string | null>(null);
  // The same two levels entities.route.ts asks for. Firm staff (Professional
  // Moderator and up) may add a client; editing, archiving, deleting and
  // resetting a login need a Professional Admin. Nobody is shown an action
  // they would be refused for pressing.
  const role = useCurrentRole();
  const canCreate = hasMinRole(role, 'moderator.manage');
  const canManage = hasMinRole(role, 'admin.tenant');

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    const t = setTimeout(() => {
      adminFetchJson<List>('/api/entities', { query: { q, status } })
        .then((d) => !cancel && setData(d))
        .finally(() => !cancel && setLoading(false));
    }, q ? 250 : 0);
    return () => { cancel = true; clearTimeout(t); };
  }, [q, status, refreshKey]);

  const refresh = () => setRefreshKey((k) => k + 1);

  // adminFetchJson, not adminFetch: it throws on a refusal. These used to
  // ignore the response, so a 403 closed the dialog as if it had worked.
  const doUnarchive = async (e: Entity) => {
    setFailed(null);
    try { await adminFetchJson(`/api/entities/${e.id}/unarchive`, { method: 'POST' }); setView(null); refresh(); }
    catch (err) { setFailed(said(err, 'Could not unarchive this entity.')); }
  };
  const requestArchive = (e: Entity) => (e.status === 'archived' ? doUnarchive(e) : setConfirm({ kind: 'archive', entity: e }));
  const requestDelete = (e: Entity) => setConfirm({ kind: 'delete', entity: e });

  const resetMember = async (entityId: string, m: Member) => {
    setFailed(null);
    try {
      const res = await adminFetchJson<{ email: string; tempPassword: string }>(`/api/entities/${entityId}/members/${m.id}/reset-password`, { method: 'POST' });
      setReveal({ title: 'Password reset', creds: [{ role: m.roleLabel, email: res.email, tempPassword: res.tempPassword }] });
    } catch (err) { setView(null); setFailed(said(err, 'Could not reset the password.')); }
  };

  const runConfirm = async () => {
    if (!confirm) return;
    const e = confirm.entity;
    setFailed(null);
    try {
      if (confirm.kind === 'delete') await adminFetchJson(`/api/entities/${e.id}`, { method: 'DELETE' });
      else await adminFetchJson(`/api/entities/${e.id}/archive`, { method: 'POST' });
    } catch (err) {
      // Said twice on purpose: in the dialog the person is looking at (it
      // catches the rethrow and stays open), and on the page behind it, so
      // the reason is still there once the dialog is closed.
      setFailed(said(err, confirm.kind === 'delete' ? 'Could not delete this entity.' : 'Could not archive this entity.'));
      throw err;
    }
    setConfirm(null); setView(null); refresh();
  };

  return (
    <>
      <SEO title="Entities" />
      <AdminPageHeader title="Entities" breadcrumbs={[{ label: 'oddits' }, { label: 'Entities' }]} />

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-full max-w-xs">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search entities…"
              className="w-full rounded-xl border border-ink-200 bg-paper py-2.5 pl-10 pr-4 text-sm shadow-sm focus:border-inkblack focus:outline-none focus:ring-2 focus:ring-highlighter" />
          </div>
          <select value={status} onChange={(e) => setStatus(e.target.value)}
            className="rounded-xl border border-ink-200 bg-paper py-2.5 pl-3 pr-8 text-sm shadow-sm focus:border-inkblack focus:outline-none focus:ring-2 focus:ring-highlighter">
            <option value="">All</option>
            <option value="active">Active</option>
            <option value="archived">Archived</option>
          </select>
        </div>
        {canCreate && (
          <button onClick={() => setCreating(true)}
            className="inline-flex items-center gap-2 rounded-full bg-inkblack px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:-translate-y-0.5">
            <Plus className="h-4 w-4" /> New entity
          </button>
        )}
      </div>

      {failed && (
        <div role="alert" className="mb-4 flex items-start justify-between gap-3 rounded-xl border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700">
          <span>{failed}</span>
          <button onClick={() => setFailed(null)} title="Dismiss" className="shrink-0 text-danger-600 hover:text-danger-700"><X className="h-4 w-4" /></button>
        </div>
      )}

      {loading && !data ? (
        <PageLoading label="Loading entities" />
      ) : data && data.items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-ink-300 bg-ink-50/50 px-6 py-16 text-center">
          <span className="mx-auto grid size-12 place-items-center rounded-2xl bg-inkblack text-white"><Building2 className="h-6 w-6" /></span>
          <h3 className="mt-5 text-lg font-semibold text-ink-900">No entities yet</h3>
          {canCreate ? (
            <>
              <p className="mt-1.5 text-sm text-ink-600">Create your first client entity, then import its Tally data.</p>
              <button onClick={() => setCreating(true)} className="mt-6 inline-flex items-center gap-2 rounded-full bg-inkblack px-5 py-2.5 text-sm font-semibold text-white"><Plus className="h-4 w-4" /> New entity</button>
            </>
          ) : (
            <p className="mt-1.5 text-sm text-ink-600">Ask your firm admin to add a client.</p>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-ink-200 bg-paper">
          <table className="w-full min-w-[46rem] text-sm">
            <thead>
              <tr className="border-b border-ink-100 text-left text-xs uppercase tracking-wide text-ink-400">
                <th className="px-5 py-3 font-medium">Entity</th>
                <th className="px-5 py-3 font-medium">Logins</th>
                <th className="px-5 py-3 font-medium">Data</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-50">
              {data?.items.map((e) => (
                <tr key={e.id} onClick={() => setView({ entity: e, editing: false })} className="cursor-pointer hover:bg-ink-50/60">
                  <td className="px-5 py-3 font-medium text-ink-900">{e.name}</td>
                  <td className="px-5 py-3 text-ink-600">{e.members.length ? `${e.members.length} login${e.members.length > 1 ? 's' : ''}` : <span className="text-ink-400">No logins</span>}</td>
                  <td className="px-5 py-3 text-ink-500">{e.companies > 0 ? `${e.companies} company` : '—'}</td>
                  <td className="px-5 py-3"><StatusBadge status={e.status} /></td>
                  <td className="px-5 py-3" onClick={(ev) => ev.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      <Link to={`/admin/import?entityId=${e.id}`} title="Import data" className="grid size-8 place-items-center rounded-lg text-ink-400 no-underline transition-colors hover:bg-ink-100 hover:text-ink-700"><Upload className="h-4 w-4" /></Link>
                      {canManage && (
                        <>
                          <IconBtn title="Edit" onClick={() => setView({ entity: e, editing: true })}><Pencil className="h-4 w-4" /></IconBtn>
                          <IconBtn title={e.status === 'archived' ? 'Unarchive' : 'Archive'} onClick={() => requestArchive(e)}>{e.status === 'archived' ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}</IconBtn>
                          <IconBtn title="Delete" danger onClick={() => requestDelete(e)}><Trash2 className="h-4 w-4" /></IconBtn>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {view && (
        <EntityDrawer
          entity={view.entity}
          startEditing={view.editing && canManage}
          canManage={canManage}
          onClose={() => setView(null)}
          onSaved={refresh}
          onArchive={() => requestArchive(view.entity)}
          onDelete={() => requestDelete(view.entity)}
          onResetMember={(m) => resetMember(view.entity.id, m)}
        />
      )}
      {creating && canCreate && <CreateModal onClose={() => setCreating(false)} onCreated={(creds) => { setCreating(false); refresh(); if (creds.length) setReveal({ title: 'Entity created', creds }); }} />}
      {reveal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-ink-900/40" onClick={() => setReveal(null)} />
          <div className="relative z-10 w-full max-w-md rounded-2xl border border-ink-200 bg-paper p-7 shadow-xl">
            <CredentialReveal creds={reveal.creds} title={reveal.title} onDone={() => setReveal(null)} />
          </div>
        </div>
      )}
      {confirm && (
        <ConfirmModal
          kind={confirm.kind} entity={confirm.entity} onConfirm={runConfirm} onClose={() => setConfirm(null)}
        />
      )}
    </>
  );
}

function StatusBadge({ status }: { status: string }) {
  return status === 'active'
    ? <span className="inline-flex items-center gap-1 rounded-full bg-ok-50 px-2.5 py-1 text-xs font-medium text-ok-600"><CheckCircle2 className="h-3.5 w-3.5" /> Active</span>
    : <span className="rounded-full bg-ink-100 px-2.5 py-1 text-xs font-medium text-ink-500">Archived</span>;
}

function IconBtn({ children, title, danger, onClick }: { children: React.ReactNode; title: string; danger?: boolean; onClick: () => void }) {
  return (
    <button title={title} onClick={onClick}
      className={`grid size-8 place-items-center rounded-lg transition-colors ${danger ? 'text-ink-400 hover:bg-danger-50 hover:text-danger-600' : 'text-ink-400 hover:bg-ink-100 hover:text-ink-700'}`}>
      {children}
    </button>
  );
}

/* ── detail sidecard ───────────────────────────────────────────── */

function EntityDrawer({ entity, startEditing, canManage, onClose, onSaved, onArchive, onDelete, onResetMember }: {
  entity: Entity; startEditing: boolean; canManage: boolean; onClose: () => void; onSaved: () => void;
  onArchive: () => void; onDelete: () => void; onResetMember: (m: Member) => void;
}) {
  const EDIT_KEYS = ['name', 'ownerPhone', 'legalName', 'gstNumber', 'gstStatus', 'billingAddress', 'city', 'state', 'pincode'] as const;
  const [current, setCurrent] = useState<Entity>(entity);
  const [editing, setEditing] = useState(startEditing);
  const [form, setForm] = useState<Record<string, string>>(() => {
    const f: Record<string, string> = {};
    for (const k of EDIT_KEYS) f[k] = (entity[k as keyof Entity] as string) ?? '';
    return f;
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [gstin, setGstin] = useState(entity.gstNumber ?? '');
  const [gstLoading, setGstLoading] = useState(false);
  const [gstMsg, setGstMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);

  const set = (k: string, v: string) => setForm((p) => ({ ...p, [k]: v }));
  const val = (k: keyof Entity) => (editing ? form[k as string] ?? '' : (current[k] as string) ?? '');

  const lookupGst = async () => {
    const g = gstin.trim().toUpperCase();
    if (g.length !== 15) { setGstMsg({ tone: 'err', text: 'Enter a 15-character GSTIN.' }); return; }
    setGstLoading(true); setGstMsg(null);
    try {
      const res = await adminFetchJson<{ data: any }>(`/api/gst/lookup/${encodeURIComponent(g)}`);
      const d = res.data;
      setForm((p) => ({
        ...p,
        name: d.tradeName || d.legalName || p.name,
        legalName: d.legalName ?? p.legalName,
        gstNumber: d.gstin ?? p.gstNumber,
        gstStatus: d.businessType ?? p.gstStatus,
        billingAddress: d.address?.full ?? p.billingAddress,
        city: d.address?.city ?? p.city,
        state: d.address?.state ?? p.state,
        pincode: d.address?.pincode ?? p.pincode,
      }));
      setGstMsg({ tone: 'ok', text: `${d.legalName}${d.status ? ` · ${d.status}` : ''} — fields updated` });
    } catch (e: any) {
      setGstMsg({ tone: 'err', text: e.message?.replace(/^\d+\s/, '') || 'Lookup failed' });
    } finally { setGstLoading(false); }
  };

  const save = async () => {
    setError(null); setSaving(true);
    try {
      await adminFetchJson(`/api/entities/${current.id}`, { method: 'PATCH', body: JSON.stringify(form) });
      setCurrent((c) => ({ ...c, ...(form as any) }));
      setEditing(false); onSaved();
    } catch (e: any) { setError(e.message?.replace(/^\d+\s/, '') || 'Could not save'); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-40">
      <div className="absolute inset-0 bg-ink-900/40" onClick={onClose} />
      <aside className="absolute inset-y-0 right-0 flex w-full max-w-md flex-col border-l border-ink-200 bg-paper shadow-xl">
        <div className="flex items-start justify-between border-b border-ink-100 px-6 py-5">
          <div className="flex min-w-0 items-center gap-3">
            <span className="grid size-11 shrink-0 place-items-center rounded-xl bg-inkblack text-sm font-semibold text-white">{current.name.slice(0, 2).toUpperCase()}</span>
            <div className="min-w-0"><h3 className="truncate text-base font-semibold text-ink-900">{current.name}</h3><div className="mt-1"><StatusBadge status={current.status} /></div></div>
          </div>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-700"><X className="h-5 w-5" /></button>
        </div>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-6 py-5">
          {error && <div className="rounded-xl border border-danger-200 bg-danger-50 px-3.5 py-2.5 text-sm text-danger-700">{error}</div>}

          {!editing && (
            <Link to={`/admin/import?entityId=${current.id}`} className="flex w-full items-center justify-center gap-2 rounded-xl border border-highlighter bg-highlighter-40 px-4 py-3 text-sm font-semibold text-inkblack no-underline transition hover:bg-highlighter-80">
              <Upload className="h-4 w-4" /> Import Tally data
            </Link>
          )}

          {editing && (
            <div className="rounded-xl border border-ink-200 bg-ink-50/60 p-3.5">
              <label className="text-sm font-medium text-ink-700">Pull from GST</label>
              <div className="mt-1.5 flex gap-2">
                <input value={gstin} maxLength={15} onChange={(e) => setGstin(e.target.value.toUpperCase())}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); lookupGst(); } }}
                  placeholder="29ABCDE1234F1Z5"
                  style={{ outline: 'none', boxShadow: 'none' }}
                  className="min-w-0 flex-1 rounded-lg border border-ink-200 bg-paper px-3 py-2 font-mono text-sm text-ink-900 placeholder:font-sans placeholder:text-ink-400 focus:border-ink-300 focus-visible:outline-none" />
                <button type="button" onClick={lookupGst} disabled={gstLoading}
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-ink-900 px-3.5 text-sm font-semibold text-white transition hover:bg-ink-800 disabled:opacity-60">
                  {gstLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Fetch
                </button>
              </div>
              {gstMsg && <p className={`mt-1.5 text-xs ${gstMsg.tone === 'ok' ? 'text-ok-600' : 'text-danger-600'}`}>{gstMsg.text}</p>}
            </div>
          )}

          <DSection title="Entity">
            {editing ? (
              <>
                <LabeledInput label="Name" value={val('name')} onChange={(v) => set('name', v)} />
                <LabeledInput label="Phone" value={val('ownerPhone')} onChange={(v) => set('ownerPhone', v)} />
                <LabeledInput label="Legal name" value={val('legalName')} onChange={(v) => set('legalName', v)} />
                <LabeledInput label="GST number" value={val('gstNumber')} onChange={(v) => set('gstNumber', v)} />
                <LabeledInput label="GST status" value={val('gstStatus')} onChange={(v) => set('gstStatus', v)} />
                <LabeledInput label="Address" value={val('billingAddress')} onChange={(v) => set('billingAddress', v)} />
                <LabeledInput label="City" value={val('city')} onChange={(v) => set('city', v)} />
                <LabeledInput label="State" value={val('state')} onChange={(v) => set('state', v)} />
                <LabeledInput label="Pincode" value={val('pincode')} onChange={(v) => set('pincode', v)} />
              </>
            ) : (
              <>
                <Row label="Name">{current.name}</Row>
                <Row label="Phone">{current.ownerPhone || '—'}</Row>
                {current.legalName && <Row label="Legal name">{current.legalName}</Row>}
                {current.gstNumber && <Row label="GSTIN">{current.gstNumber}</Row>}
                {current.gstStatus && <Row label="GST status">{current.gstStatus}</Row>}
                {(current.billingAddress || current.city || current.state || current.pincode) && (
                  <Row label="Address">{[current.billingAddress, current.city, current.state, current.pincode].filter(Boolean).join(', ')}</Row>
                )}
                <Row label="Data">{current.companies > 0 ? `${current.companies} Tally company` : 'Not imported yet'}</Row>
                <Row label="Created">{fmtDate(current.createdAt)}</Row>
              </>
            )}
          </DSection>

          {!editing && (
            <DSection title="Logins">
              {current.members.length === 0 ? (
                <p className="py-2 text-sm text-ink-400">No logins. Recreate the entity with Admin/Moderator emails to add them.</p>
              ) : (
                current.members.map((m) => (
                  <div key={m.id} className="flex items-start justify-between gap-3 py-2.5">
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 text-sm font-medium text-ink-800"><ShieldCheck className="h-3.5 w-3.5 text-ink-400" /> {m.roleLabel}</div>
                      <div className="truncate text-xs text-ink-500">{m.email}</div>
                      {!m.isActive && <div className="mt-0.5 inline-flex items-center gap-1 text-xs text-danger-600"><UserX className="h-3 w-3" /> Disabled</div>}
                    </div>
                    {canManage && <button onClick={() => onResetMember(m)} title="Reset password" className="shrink-0 rounded-lg border border-ink-200 px-2.5 py-1.5 text-xs font-semibold text-ink-600 transition hover:bg-ink-50 hover:text-ink-900"><KeyRound className="h-3.5 w-3.5" /></button>}
                  </div>
                ))
              )}
            </DSection>
          )}
        </div>

        {/* Edit, archive and delete are a Professional Admin's — see canManage. */}
        {canManage && <div className="flex flex-wrap items-center gap-2 border-t border-ink-100 px-6 py-4">
          {editing ? (
            <>
              <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-inkblack px-5 py-2 text-sm font-semibold text-white disabled:opacity-60">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save changes</button>
              <button onClick={() => { setEditing(false); setForm(() => { const f: Record<string, string> = {}; for (const k of EDIT_KEYS) f[k] = (current[k as keyof Entity] as string) ?? ''; return f; }); setGstMsg(null); setError(null); }} disabled={saving} className="rounded-full border border-ink-300 bg-paper px-4 py-2 text-sm font-semibold text-ink-700 hover:bg-ink-50">Cancel</button>
            </>
          ) : (
            <>
              <button onClick={() => setEditing(true)} className="inline-flex items-center gap-2 rounded-full bg-inkblack px-4 py-2 text-sm font-semibold text-white"><Pencil className="h-4 w-4" /> Edit</button>
              <button onClick={onArchive} className="inline-flex items-center gap-2 rounded-full border border-ink-300 bg-paper px-4 py-2 text-sm font-semibold text-ink-700 hover:bg-ink-50">{current.status === 'archived' ? <><ArchiveRestore className="h-4 w-4" /> Unarchive</> : <><Archive className="h-4 w-4" /> Archive</>}</button>
              <button onClick={onDelete} className="ml-auto inline-flex items-center gap-2 rounded-full border border-danger-200 bg-paper px-4 py-2 text-sm font-semibold text-danger-600 hover:bg-danger-50"><Trash2 className="h-4 w-4" /> Delete</button>
            </>
          )}
        </div>}
      </aside>
    </div>
  );
}

function DSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-ink-200 p-4">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-400">{title}</p>
      <div className="divide-y divide-ink-50">{children}</div>
    </div>
  );
}
function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3 py-2 text-sm">
      <span className="w-24 shrink-0 text-ink-500">{label}</span>
      <span className="min-w-0 flex-1 break-words font-medium text-ink-800">{children}</span>
    </div>
  );
}
function LabeledInput({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div className="flex items-center gap-3 py-2 text-sm">
      <span className="w-24 shrink-0 text-ink-500">{label}</span>
      <input value={value} onChange={(e) => onChange(e.target.value)} style={{ outline: 'none', boxShadow: 'none' }} className="min-w-0 flex-1 rounded-lg border border-ink-200 bg-paper px-3 py-2 text-sm focus:border-ink-300 focus-visible:outline-none" />
    </div>
  );
}

/* ── confirm ───────────────────────────────────────────────────── */

function ConfirmModal({ kind, entity, onConfirm, onClose }: { kind: 'delete' | 'archive'; entity: Entity; onConfirm: () => Promise<void>; onClose: () => void }) {
  const cfg = kind === 'delete'
    ? { title: 'Delete entity', confirmLabel: 'Delete', danger: true, icon: Trash2, body: <>This permanently deletes <strong>{entity.name}</strong>, its logins and all imported Tally data. This cannot be undone.</> }
    : { title: 'Archive entity', confirmLabel: 'Archive', danger: false, icon: Archive, body: <>Archiving disables <strong>{entity.name}</strong>'s logins until you unarchive it.</> };
  const Icon = cfg.icon;
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ok = text.trim() === entity.name;
  const go = async () => { if (!ok || busy) return; setBusy(true); setError(null); try { await onConfirm(); } catch (e: any) { setError(e.message?.replace(/^\d+\s/, '') || 'Something went wrong'); setBusy(false); } };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-ink-900/40" onClick={onClose} />
      <div className="relative z-10 w-full max-w-md rounded-2xl border border-ink-200 bg-paper p-7 shadow-xl">
        <div className="mb-4 flex items-center gap-3">
          <span className={`grid size-10 place-items-center rounded-xl ${cfg.danger ? 'bg-danger-50 text-danger-600' : 'bg-ink-100 text-ink-600'}`}><Icon className="h-5 w-5" /></span>
          <h3 className="text-lg font-semibold text-ink-900">{cfg.title}</h3>
        </div>
        <p className="text-sm text-ink-600">{cfg.body}</p>
        {error && <div className="mt-3 rounded-xl border border-danger-200 bg-danger-50 px-3.5 py-2.5 text-sm text-danger-700">{error}</div>}
        <label className="mt-4 block text-sm text-ink-600">Type <span className="font-semibold text-ink-900">{entity.name}</span> to confirm</label>
        <input autoFocus value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') go(); }} placeholder={entity.name}
          style={{ outline: 'none', boxShadow: 'none' }}
          className="mt-1.5 w-full rounded-xl border border-ink-200 bg-paper px-4 py-2.5 text-sm text-ink-900 placeholder:text-ink-400 focus:border-ink-300 focus-visible:outline-none" />
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} disabled={busy} className="rounded-full border border-ink-300 bg-paper px-5 py-2.5 text-sm font-semibold text-ink-700 hover:bg-ink-50">Cancel</button>
          <button onClick={go} disabled={!ok || busy} className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50 ${cfg.danger ? 'bg-danger-600 hover:bg-danger-700' : 'bg-inkblack'}`}>{busy && <Loader2 className="h-4 w-4 animate-spin" />} {cfg.confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

/* ── create (name + two fixed logins) ──────────────────────────── */

function CreateModal({ onClose, onCreated }: { onClose: () => void; onCreated: (creds: Array<{ role: string; email: string; tempPassword: string }>) => void }) {
  const [form, setForm] = useState({ name: '', ownerPhone: '', adminName: '', adminEmail: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  // GSTIN-driven autofill.
  const [gstin, setGstin] = useState('');
  const [gst, setGst] = useState<Record<string, string> | null>(null);
  const [gstLoading, setGstLoading] = useState(false);
  const [gstMsg, setGstMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);
  const setG = (k: string, v: string) => setGst((g) => ({ ...(g ?? {}), [k]: v }));

  const lookupGst = async () => {
    const g = gstin.trim().toUpperCase();
    if (g.length !== 15) { setGstMsg({ tone: 'err', text: 'Enter a 15-character GSTIN.' }); return; }
    setGstLoading(true); setGstMsg(null);
    try {
      const res = await adminFetchJson<{ data: any }>(`/api/gst/lookup/${encodeURIComponent(g)}`);
      const d = res.data;
      setForm((f) => ({ ...f, name: d.tradeName || d.legalName || f.name }));
      setGst({
        gstNumber: d.gstin, legalName: d.legalName ?? '', gstStatus: d.businessType ?? '',
        billingAddress: d.address?.full ?? '', city: d.address?.city ?? '', state: d.address?.state ?? '', pincode: d.address?.pincode ?? '',
      });
      setGstMsg({ tone: 'ok', text: `${d.legalName}${d.status ? ` · ${d.status}` : ''}` });
    } catch (e: any) {
      setGst(null);
      setGstMsg({ tone: 'err', text: e.message?.replace(/^\d+\s/, '') || 'Lookup failed' });
    } finally { setGstLoading(false); }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setError(null); setSaving(true);
    try {
      const body = {
        name: form.name, ownerPhone: form.ownerPhone,
        admin: form.adminEmail ? { name: form.adminName, email: form.adminEmail } : undefined,
        ...(gst ?? {}),
      };
      const res = await adminFetchJson<{ credentials: Array<{ role: string; email: string; tempPassword: string }> }>('/api/entities', { method: 'POST', body: JSON.stringify(body) });
      onCreated(res.credentials ?? []);
    } catch (e: any) { setError(e.message?.replace(/^\d+\s/, '') || 'Something went wrong'); setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-ink-900/40" onClick={onClose} />
      <div className="relative z-10 flex max-h-[88vh] w-full max-w-md flex-col rounded-2xl border border-ink-200 bg-paper shadow-xl">
        <div className="flex items-center justify-between border-b border-ink-100 px-7 py-5">
          <h3 className="text-lg font-semibold text-ink-900">New entity</h3>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-700"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-7 py-5">
            {error && <div className="rounded-xl border border-danger-200 bg-danger-50 px-3.5 py-2.5 text-sm text-danger-700">{error}</div>}

            {/* GSTIN autofill */}
            <div className="rounded-xl border border-ink-200 bg-ink-50/60 p-3.5">
              <label className="text-sm font-medium text-ink-700">GSTIN <span className="font-normal text-ink-400">— autofills details</span></label>
              <div className="mt-1.5 flex gap-2">
                <input value={gstin} maxLength={15} onChange={(e) => setGstin(e.target.value.toUpperCase())}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); lookupGst(); } }}
                  placeholder="29ABCDE1234F1Z5"
                  style={{ outline: 'none', boxShadow: 'none' }}
                  className="min-w-0 flex-1 rounded-xl border border-ink-200 bg-paper px-3.5 py-2.5 font-mono text-sm text-ink-900 placeholder:font-sans placeholder:text-ink-400 focus:border-ink-300 focus-visible:outline-none" />
                <button type="button" onClick={lookupGst} disabled={gstLoading}
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-ink-900 px-4 text-sm font-semibold text-white transition hover:bg-ink-800 disabled:opacity-60">
                  {gstLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Fetch
                </button>
              </div>
              {gstMsg && <p className={`mt-1.5 text-xs ${gstMsg.tone === 'ok' ? 'text-ok-600' : 'text-danger-600'}`}>{gstMsg.text}</p>}
            </div>

            <Field label="Entity organization name" value={form.name} onChange={(v) => set('name', v)} required />
            <Field label="Entity admin name" value={form.adminName} onChange={(v) => set('adminName', v)} />
            <Field label="Entity admin email (login)" type="email" value={form.adminEmail} onChange={(v) => set('adminEmail', v)} required />
            <Field label="Phone" type="tel" value={form.ownerPhone} onChange={(v) => set('ownerPhone', v)} placeholder="Optional" />

            {gst && (
              <div className="space-y-3 rounded-xl border border-ink-200 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-ink-400">Fetched from GST — review &amp; edit</p>
                <Field label="Legal name" value={gst.legalName ?? ''} onChange={(v) => setG('legalName', v)} />
                <div className="grid grid-cols-2 gap-3">
                  <Field label="GST number" value={gst.gstNumber ?? ''} onChange={(v) => setG('gstNumber', v)} />
                  <Field label="GST status" value={gst.gstStatus ?? ''} onChange={(v) => setG('gstStatus', v)} />
                </div>
                <Field label="Billing address" value={gst.billingAddress ?? ''} onChange={(v) => setG('billingAddress', v)} />
                <div className="grid grid-cols-3 gap-3">
                  <Field label="City" value={gst.city ?? ''} onChange={(v) => setG('city', v)} />
                  <Field label="State" value={gst.state ?? ''} onChange={(v) => setG('state', v)} />
                  <Field label="Pincode" value={gst.pincode ?? ''} onChange={(v) => setG('pincode', v)} />
                </div>
              </div>
            )}

            <div className="flex items-start gap-2 rounded-xl bg-ink-50 px-3.5 py-3 text-xs text-ink-500">
              <Database className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ink-400" />
              <p>The admin email is the entity's login — a temporary password is generated and shown once. Import Tally data from the entity afterwards.</p>
            </div>
          </div>
          <div className="flex justify-end gap-2 border-t border-ink-100 px-7 py-4">
            <button type="button" onClick={onClose} className="rounded-full border border-ink-300 bg-paper px-5 py-2.5 text-sm font-semibold text-ink-700 hover:bg-ink-50">Cancel</button>
            <button type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-inkblack px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create entity</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', required, placeholder }: {
  label: string; value: string; onChange: (v: string) => void; type?: string; required?: boolean; placeholder?: string;
}) {
  return (
    <div className="space-y-2.5">
      <label className="text-sm font-medium text-ink-700">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} placeholder={placeholder}
        style={{ outline: 'none', boxShadow: 'none' }}
        className="w-full rounded-xl border border-ink-200 bg-paper px-4 py-3 text-sm text-ink-900 placeholder:text-ink-400 focus:border-ink-300 focus-visible:outline-none" />
    </div>
  );
}
